import { useCallback, useRef, useState } from "react";
import { NativeScrollEvent, NativeSyntheticEvent } from "react-native";

const MIN_SCROLL_DELTA = 20;
const OFFSET_TO_HIDE_MENU = 35;

interface HandleOnScrollFunction {
  (event: NativeSyntheticEvent<NativeScrollEvent>): void;
}

interface UseVisibilityForVerticalScrollResult {
  readonly visible: boolean;
  readonly handleOnScroll: HandleOnScrollFunction;
}

interface UseVisibilityForVerticalScrollFunction {
  (): UseVisibilityForVerticalScrollResult;
}

const useVisibilityForVerticalScroll: UseVisibilityForVerticalScrollFunction = () => {
  const [visible, setVisible] = useState(true);
  const prevScrollPosition = useRef(0);
  const prevViewportHeight = useRef<number | undefined>(undefined);

  const handleOnScroll: HandleOnScrollFunction = useCallback((event) => {
    const viewportHeight = event.nativeEvent.layoutMeasurement.height;
    const pageHeightSize = event.nativeEvent.contentSize.height - viewportHeight;
    const scrollY = Math.max(0, event.nativeEvent.contentOffset.y);
    const currentScrollPosition = Math.min(scrollY, pageHeightSize);

    const viewportResized = prevViewportHeight.current !== undefined && prevViewportHeight.current !== viewportHeight;
    prevViewportHeight.current = viewportHeight;

    if (viewportResized) {
      prevScrollPosition.current = currentScrollPosition;

      return;
    }

    if (currentScrollPosition < OFFSET_TO_HIDE_MENU) {
      setVisible(true);
    }

    if (Math.abs(prevScrollPosition.current - currentScrollPosition) < MIN_SCROLL_DELTA) {
      return;
    }

    setVisible(prevScrollPosition.current > currentScrollPosition);

    prevScrollPosition.current = currentScrollPosition;
  }, []);

  return { visible, handleOnScroll };
};

export { useVisibilityForVerticalScroll };
